import ListAltIcon from '@mui/icons-material/ListAlt'
import MapIcon from '@mui/icons-material/Map'
import { IconButton } from '@mui/material'
import Tooltip from '@mui/material/Tooltip'
import { ListItemIcon, ListItemText, MenuItem } from '@mui/material'
import { MainApi } from 'api/MainApi'
import { StationApi } from 'api/StationApi'
import clsx from 'clsx'
import Box from 'components/Box/Box'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import { StyledDropdown } from 'components/Dropdown/StyledDropdown'
import Loader from 'components/Loader/Loader'
import MapboxCommon from 'components/MapboxMap/MapboxCommon'
import ScenarioServicePointsOnMap from 'components/ScenarioServicePointsOnMap/ScenarioServicePointsOnMap'
import MissionStats from 'components/MissionStats/MissionStats'
import { MaintenanceItem, MissionTableRow, MissionsTable } from 'components/MissionsTable/MissionsTable'
import ResourceProfileSummary from 'components/ResourceProfileSummary/ResourceProfileSummary'
import PrerequisitesSettingsModal from 'components/PrerequisitesSettings/PrerequisitesSettingsModal'
import RobotTypeLabel from 'components/RobotTypeLabel/RobotTypeLabel'
import Title from 'components/Title/Title'
import { FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS } from 'config/featureFlags'
import { Feature } from 'geojson'
import { useIsMobile } from 'hooks/useIsMobile'
import { ReactComponent as DebrisDumpSvg } from 'images/icons/debris_dump.svg'
import { ReactComponent as GearSvg } from 'images/icons/gear.svg'
import { ReactComponent as LotNotSetSvg } from 'images/icons/lot_not_set.svg'
import { ReactComponent as MaintenanceUniversalSvg } from 'images/icons/maintenance_universal.svg'
import { ReactComponent as ParkingSvg } from 'images/icons/parking.svg'
import { ReactComponent as ChargingSvg } from 'images/icons/plug_in.svg'
import { ReactComponent as RefillingSvg } from 'images/icons/refilling.svg'
import { ReactComponent as SandClockSvg } from 'images/icons/sand_clock.svg'
import { ReactComponent as WashingSvg } from 'images/icons/washing.svg'
import { ReactComponent as WasteWaterDrainSvg } from 'images/icons/waste_water_drain.svg'
import CleaningPointsOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningPointsOnMap'
import CleaningRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningRoutesOnMap'
import CleaningZonesNamesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesNamesOnMap'
import CleaningZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/CleaningZonesOnMap'
import MissionStartFinishPoints from 'pages/Mission/MissionView/MissionMap/Layers/MissionStartFinishPoints'
import RestrictZonesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/RestrictZonesOnMap'
import TransferRoutesOnMap from 'pages/Mission/MissionView/MissionMap/Layers/TransferRoutesOnMap'
import SelectMissionModal from 'pages/Robot/RobotView/RobotLeftBlock/RobotDetails/RobotMissionMainStatus/SelectMissionModal/SelectMissionModal'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { MapboxMap } from 'react-map-gl'
import { PROGRESS_TYPES } from 'utils/constants'
import { AutonomicsResourceProfile, IMapboxElement, IMission, IMissionInfo } from 'utils/interfaces'
import { mergeOdysseySettings, OdysseyStartSettings } from 'utils/odysseySettings'
import { zoomMapTo } from 'utils/mapUtils'
import {
  ACTION_TYPE_LABELS,
  ActionType,
  ILotModelConfig,
  IServicePoint,
  LotType,
  SERVICE_POINT_KINDS,
  allowsEmptyScenarioActionSelection,
  autoFillScenarioActionsAfterChargingStrip,
  filterCatalogActionsForRobotType,
  isDrainOnlyMaintenancePointType,
  isSweeperRobotType,
  legacyToServicePoints,
  resolveScenarioStationLotAllowedActions,
} from 'utils/servicePointsCatalog'
import { findLotModelByNumber, washingLotVisibleForScenarioRobot } from 'utils/stationLotModel'
import { VENDOR } from 'utils/vendorDisplayName'
import {
  buildScenarioMaintenance,
  CHARGING_MUST_BE_LAST_MSG,
  messageForEmptyScenarioActions,
  chargingPlacementError,
  formatApiErrorDetails,
  hasChargingNotAtEnd,
  isChargingAnchor,
  shiftEndBlockServicePoints,
} from 'utils/scenarioEditorMaintenance'

import style from './ScenarioView.module.css'
import MapTableToggleStyle from 'styles/MapTableToggle.module.css'

interface ScenarioData {
  id: number
  name: string
  description?: string
  missions_ids: number[]
  missions_locations?: (string | null)[]
  mission_equipment_setups?: Record<string, { front_lights?: boolean }>
  robot_type?: string
  missions_type_inconsistent?: boolean
  service_points_incomplete?: boolean
  missions_statuses?: string[]
  overall_execution_time?: number
  overall_length?: number
  overall_cleaned_area?: number
  maintenance?: {
    parking?: boolean
    charging?: boolean
    washing?: boolean
    waiting_points?: { position: number; time: number }[]
    service_points?: unknown
  }
  settings?: OdysseyStartSettings | null
  resource_profile?: AutonomicsResourceProfile | null
  facility_id?: number
}

// Global promise cache to deduplicate fetches across components and remounts
const missionPromises: Record<number, Promise<IMission>> = {}

const ScenarioView = ({
  scenario,
  onUpdated,
}: {
  scenario: ScenarioData
  onUpdated: (background?: boolean) => void
}) => {
  const { t } = useTranslation()
  const [highlightedMissionId, setHighlightedMissionId] = useState<number | undefined>(undefined)
  const [selectedMissionIndices, setSelectedMissionIndices] = useState<Set<number>>(new Set())
  const hasInitializedSelection = useRef(false)
  const mapRef = useRef<IMapboxElement>(null)
  const [map, setMap] = useState<MapboxMap | undefined>(undefined)

  // Add mission dialog
  const [addMissionDialog, setAddMissionDialog] = useState(false)

  // Mission list state
  const [missions, setMissions] = useState<IMissionInfo[]>([])
  const [missionsProgress, setMissionsProgress] = useState(PROGRESS_TYPES.IDLE)

  const isMobile = useIsMobile()
  const [showTable, setShowTable] = useState(true)

  // Selected mission details for the map
  const [selectedMissionDetails, setSelectedMissionDetails] = useState<IMission | undefined>(undefined)

  const missionCacheRef = useRef<Record<number, IMission>>({})
  const [cacheVersion, setCacheVersion] = useState(0)
  const [addSPAnchorEl, setAddSPAnchorEl] = useState<null | HTMLElement>(null)
  const [lotModels, setLotModels] = useState<ILotModelConfig[]>([])
  const [maintenancePoints, setMaintenancePoints] = useState<any[]>([])
  const [facilityLots, setFacilityLots] = useState<any[]>([])
  const [facilityStations, setFacilityStations] = useState<
    { id: number; name: string; station_id: string; point?: any }[]
  >([])
  const [localServicePoints, setLocalServicePoints] = useState<IServicePoint[]>([])
  const [highlightedSpIndex, setHighlightedSpIndex] = useState<number | undefined>(undefined)
  const [warningMsg, setWarningMsg] = useState<string | null>(null)
  const [prerequisites, setPrerequisites] = useState<OdysseyStartSettings>(() =>
    mergeOdysseySettings(scenario.settings, scenario.resource_profile)
  )
  const [prerequisitesModalOpen, setPrerequisitesModalOpen] = useState(false)
  const bottomRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setPrerequisites(mergeOdysseySettings(scenario.settings, scenario.resource_profile))
  }, [scenario.id, scenario.settings, scenario.resource_profile])

  const savePrerequisites = async (next: OdysseyStartSettings) => {
    setPrerequisites(next)
    await MainApi.patchScenarios(scenario.id, { settings: next })
    onUpdated(true)
  }

  useEffect(() => {
    MainApi.fetchLotModels().then(setLotModels).catch(console.error)
    if (scenario.facility_id) {
      MainApi.fetchMaintenancePoints(scenario.facility_id as number)
        .then(setMaintenancePoints)
        .catch(console.error)
      MainApi.fetchFacilityLots(scenario.facility_id as number)
        .then(setFacilityLots)
        .catch(console.error)
      StationApi.fetchStations({ facilityId: scenario.facility_id as number })
        .then(setFacilityStations)
        .catch(console.error)
    } else {
      setMaintenancePoints([])
      setFacilityLots([])
      setFacilityStations([])
    }
  }, [scenario.facility_id])

  useEffect(() => {
    // Empty action_type drafts are persisted (UX-2) — do not merge "pending" empties from
    // prev state or they duplicate after PATCH/reload when the same SP is already in maintenance.
    const fromBackend = legacyToServicePoints(scenario.maintenance, scenario.missions_ids.length)
    fromBackend.sort((a: IServicePoint, b: IServicePoint) => {
      if (a.position !== b.position) return a.position - b.position
      return a.order - b.order
    })
    setLocalServicePoints(fromBackend)
  }, [scenario.maintenance, scenario.missions_ids.length])

  const handleOpenAddSPMenu = (event: React.MouseEvent<HTMLButtonElement>) => {
    setAddSPAnchorEl(event.currentTarget)
  }

  const handleCloseAddSPMenu = () => {
    setAddSPAnchorEl(null)
  }

  const saveMaintenanceServicePoints = async (points: IServicePoint[]) => {
    let hasError = false
    let wasModified = false
    const hasChargingLotInScenario = points.some(p => {
      if (p.kind !== SERVICE_POINT_KINDS.STATION_LOT) return false
      const config = findLotModelByNumber(lotModels, p.lot_model)
      return config?.type === LotType.CHARGING
    })
    const cleanedPoints = points.map((sp, index) => {
      let actions = filterCatalogActionsForRobotType(sp.action_type, scenario.robot_type)
      if (actions.length !== sp.action_type.length) {
        wasModified = true
      }
      const isLast = index === points.length - 1 && sp.position >= missions.length
      // Do not auto-clear CHARGING: while the action is on, the SP must stay last
      // (move/drag is blocked). Operator can remove CHARGING manually on non-charging lots.
      const filledActions = autoFillScenarioActionsAfterChargingStrip(
        sp,
        actions,
        lotModels,
        isLast,
        scenario.robot_type
      )
      if (filledActions.length !== actions.length) {
        actions = filledActions
        wasModified = true
      }
      if (actions.length === 0) {
        if (
          !allowsEmptyScenarioActionSelection(sp, lotModels, maintenancePoints, {
            isLast,
            hasChargingLotInScenario,
            robotType: scenario.robot_type,
          })
        ) {
          hasError = true
        }
      }
      return { ...sp, action_type: actions }
    })

    if (wasModified) {
      setLocalServicePoints(cleanedPoints)
    }

    // Persist drafts with empty action_type (UX-2: REFILLING / universal MP before selection).
    if (hasError) {
      setWarningMsg(
        messageForEmptyScenarioActions(cleanedPoints, lotModels, maintenancePoints, {
          missionCount: missions.length,
          hasChargingLotInScenario,
          robotType: scenario.robot_type,
        })
      )
      return false
    }

    // Charging dock / CHARGING action must be the last scenario row (no mission after it).
    const fullOrder: Array<{ type: 'mission' } | { type: 'sp'; sp: IServicePoint }> = []
    for (let i = 0; i < missions.length; i++) {
      cleanedPoints
        .filter(sp => sp.position === i)
        .sort((a, b) => a.order - b.order)
        .forEach(sp => fullOrder.push({ type: 'sp', sp }))
      fullOrder.push({ type: 'mission' })
    }
    cleanedPoints
      .filter(sp => sp.position >= missions.length)
      .sort((a, b) => a.order - b.order)
      .forEach(sp => fullOrder.push({ type: 'sp', sp }))
    if (hasChargingNotAtEnd(fullOrder, lotModels)) {
      setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
      return false
    }

    const maintenance = buildScenarioMaintenance(
      scenario.maintenance && typeof scenario.maintenance === 'object' ? (scenario.maintenance as any) : {},
      cleanedPoints,
      missions.length
    )
    const chargingErr = chargingPlacementError(cleanedPoints, missions.length)
    if (chargingErr) {
      setWarningMsg(chargingErr)
      return false
    }

    try {
      await MainApi.patchScenarios(scenario.id, { maintenance } as any)
      onUpdated(true)
      return true
    } catch (error) {
      console.error('Error saving service points:', error)
      setWarningMsg(formatApiErrorDetails(error))
      return false
    }
  }

  const handleAddSP = async (point: IServicePoint) => {
    handleCloseAddSPMenu()
    const pointWithValidPos = { ...point, position: Math.max(1, point.position) }
    let newPoints = [...localServicePoints]

    // Determine the lot type of the point being added
    const addedLotConfig = pointWithValidPos.lot_model
      ? findLotModelByNumber(lotModels, pointWithValidPos.lot_model)
      : null
    const addedLotType = addedLotConfig?.type ?? null

    if (addedLotType === LotType.CHARGING) {
      // Requirement 4: Block if a CHARGING lot type already exists
      const existingChargingLot = newPoints.find(p => {
        if (p.kind !== SERVICE_POINT_KINDS.STATION_LOT) return false
        const config = findLotModelByNumber(lotModels, p.lot_model)
        return config?.type === LotType.CHARGING
      })
      if (existingChargingLot) {
        setWarningMsg('A charging station is already present in the scenario. Remove it first')
        return
      }

      // Requirement 3: Deactivate CHARGING action on any REFILLING lots
      newPoints = newPoints.map(p => {
        if (p.kind !== SERVICE_POINT_KINDS.STATION_LOT) return p
        const config = findLotModelByNumber(lotModels, p.lot_model)
        if (config?.type === LotType.REFILLING && p.action_type.includes(ActionType.CHARGING)) {
          return { ...p, action_type: p.action_type.filter(a => a !== ActionType.CHARGING) }
        }
        return p
      })
    } else if (pointWithValidPos.action_type.includes(ActionType.CHARGING)) {
      // Non-CHARGING lot but with CHARGING action — existing dedup logic
      let blocked = false
      newPoints = newPoints.map(p => {
        if (p.action_type.includes(ActionType.CHARGING)) {
          if (p.action_type.length === 1) {
            blocked = true
            return p
          } else {
            return { ...p, action_type: p.action_type.filter(a => a !== ActionType.CHARGING) }
          }
        }
        return p
      })
      if (blocked) {
        setWarningMsg('A charging station is already present in the scenario. Remove it first')
        return
      }
    }

    if (pointWithValidPos.position >= missions.length && addedLotType !== LotType.CHARGING) {
      const lastPoint = newPoints.length > 0 ? newPoints[newPoints.length - 1] : null
      if (lastPoint && lastPoint.position >= missions.length && lastPoint.action_type.includes(ActionType.CHARGING)) {
        pointWithValidPos.order = lastPoint.order - 0.5
      }
    }

    newPoints.push(pointWithValidPos)

    // Fix `order` values
    let currentPosition = -1
    let orderCounter = 0
    newPoints.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position
      return a.order - b.order
    })

    newPoints = newPoints.map(p => {
      if (p.position !== currentPosition) {
        currentPosition = p.position
        orderCounter = 0
      }
      return { ...p, order: orderCounter++ }
    })

    setLocalServicePoints(newPoints)
    const success = await saveMaintenanceServicePoints(newPoints)
    if (success === false) {
      setLocalServicePoints(localServicePoints)
    }

    setTimeout(() => {
      bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
    }, 100)
  }

  const handleActionToggle = async (index: number, action: ActionType) => {
    const sp = localServicePoints[index]
    if (!sp) return
    if (action === ActionType.WASTE_WATER_DRAIN && isSweeperRobotType(scenario.robot_type)) {
      return
    }

    let newPoints = [...localServicePoints]

    if (action === ActionType.CHARGING && !sp.action_type.includes(ActionType.CHARGING)) {
      let blocked = false
      newPoints = newPoints.map((p, i) => {
        if (i !== index && p.action_type.includes(ActionType.CHARGING)) {
          if (p.action_type.length === 1) {
            blocked = true
            return p
          } else {
            return { ...p, action_type: p.action_type.filter(a => a !== ActionType.CHARGING) }
          }
        }
        return p
      })
      if (blocked) {
        setWarningMsg('A charging station is already present in the scenario. Remove it first')
        return
      }
    }

    const newActions = sp.action_type.includes(action)
      ? sp.action_type.filter(a => a !== action)
      : [...sp.action_type, action]

    const newSp = { ...sp, action_type: newActions }
    newPoints[index] = newSp

    setLocalServicePoints(newPoints)
    const success = await saveMaintenanceServicePoints(newPoints)
    if (success === false) {
      setLocalServicePoints(localServicePoints)
    }
  }

  const deleteServicePoint = async (index: number) => {
    const newPoints = localServicePoints.filter((_, i) => i !== index)
    // Reorder just in case
    let currentPosition = -1
    let orderCounter = 0
    const fixedPoints = newPoints.map(p => {
      if (p.position !== currentPosition) {
        currentPosition = p.position
        orderCounter = 0
      }
      return { ...p, order: orderCounter++ }
    })

    setLocalServicePoints(fixedPoints)
    const success = await saveMaintenanceServicePoints(fixedPoints)
    if (success === false) {
      setLocalServicePoints(localServicePoints)
    }
  }

  const updateWaitingTime = async (index: number, newTime: number) => {
    const sp = localServicePoints[index]
    if (!sp || sp.kind !== SERVICE_POINT_KINDS.WAIT) return
    const newSp = { ...sp, time_sec: newTime }
    let newPoints = [...localServicePoints]
    newPoints[index] = newSp
    setLocalServicePoints(newPoints)
    const success = await saveMaintenanceServicePoints(newPoints)
    if (success === false) {
      setLocalServicePoints(localServicePoints)
    }
  }

  const combinedList = useMemo(() => {
    const list: ({ type: 'mission'; idx: number } | { type: 'sp'; spIndex: number })[] = []

    for (let i = 0; i < missions.length; i++) {
      const spsAtPos = localServicePoints
        .map((sp, spIndex) => ({ sp, spIndex }))
        .filter(({ sp }) => sp.position === i)
        .sort((a, b) => a.sp.order - b.sp.order)

      spsAtPos.forEach(({ spIndex }) => {
        list.push({ type: 'sp', spIndex })
      })

      list.push({ type: 'mission', idx: i })
    }

    const postSPs = localServicePoints
      .map((sp, spIndex) => ({ sp, spIndex }))
      .filter(({ sp }) => sp.position >= missions.length)
      .sort((a, b) => a.sp.order - b.sp.order)

    postSPs.forEach(({ spIndex }) => {
      list.push({ type: 'sp', spIndex })
    })

    return list
  }, [missions, localServicePoints])

  /**
   * Derive new missions_ids, equipment_setups, and service-point positions
   * from a reordered combined list.  Used by both moveMission and handleDrop.
   */
  const deriveStateFromCombinedList = (
    newList: typeof combinedList
  ): {
    updatedIds: number[]
    newSetups: Record<string, { front_lights?: boolean }>
    newServicePoints: IServicePoint[]
    missionIndexMap: Map<number, number> // old index → new index
  } => {
    const updatedIds: number[] = []
    const missionIndexMap = new Map<number, number>()
    const currentSetups = scenario.mission_equipment_setups || {}
    const newSetups: Record<string, { front_lights?: boolean }> = {}

    // First pass: extract new mission order
    newList.forEach(item => {
      if (item.type === 'mission') {
        const newIdx = updatedIds.length
        missionIndexMap.set(item.idx, newIdx)
        updatedIds.push(scenario.missions_ids[item.idx])
        const setup = currentSetups[item.idx] || currentSetups[String(item.idx)]
        if (setup) newSetups[newIdx] = setup
      }
    })

    // Second pass: compute new service-point positions
    let currentPosition = 0
    let orderCounter = 0
    const newPoints = [...localServicePoints]

    newList.forEach(item => {
      if (item.type === 'mission') {
        const newMissionIdx = missionIndexMap.get(item.idx)!
        currentPosition = newMissionIdx + 1
        orderCounter = 0
      } else {
        const sp = newPoints[item.spIndex]
        newPoints[item.spIndex] = { ...sp, position: currentPosition, order: orderCounter++ }
      }
    })

    newPoints.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position
      return a.order - b.order
    })

    return { updatedIds, newSetups, newServicePoints: newPoints, missionIndexMap }
  }

  /** Charging dock (lot type or CHARGING action) must remain the last row. */
  const hasChargingViolation = (newList: typeof combinedList): boolean => {
    const displayOrder = newList.map(item =>
      item.type === 'mission'
        ? { type: 'mission' as const }
        : { type: 'sp' as const, sp: localServicePoints[item.spIndex] }
    )
    return hasChargingNotAtEnd(displayOrder, lotModels)
  }

  const getSPIsFirst = (spIndex: number) => {
    const idx = combinedList.findIndex(item => item.type === 'sp' && item.spIndex === spIndex)
    if (idx <= 0) return true
    const prevItem = combinedList[idx - 1]
    if (prevItem.type === 'mission' && prevItem.idx === 0) {
      return true
    }
    return false
  }

  const getSPIsLast = (spIndex: number) => {
    const idx = combinedList.findIndex(item => item.type === 'sp' && item.spIndex === spIndex)
    return idx === combinedList.length - 1
  }

  const getMissionIsFirst = (missionIndex: number) => {
    return combinedList.findIndex(item => item.type === 'mission' && item.idx === missionIndex) === 0
  }

  const getMissionIsLast = (missionIndex: number) => {
    return (
      combinedList.findIndex(item => item.type === 'mission' && item.idx === missionIndex) === combinedList.length - 1
    )
  }

  const moveServicePoint = async (index: number, direction: 'up' | 'down') => {
    const idxInList = combinedList.findIndex(item => item.type === 'sp' && item.spIndex === index)
    if (idxInList === -1) return

    const targetIdx = direction === 'up' ? idxInList - 1 : idxInList + 1
    if (targetIdx < 0 || targetIdx >= combinedList.length) return

    const newList = [...combinedList]
    ;[newList[idxInList], newList[targetIdx]] = [newList[targetIdx], newList[idxInList]]

    // Constraint: first item must be a mission (if missions exist)
    if (scenario.missions_ids.length > 0 && newList[0].type !== 'mission') {
      setWarningMsg('A scenario must begin with a mission')
      return
    }

    if (hasChargingViolation(newList)) {
      setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
      return
    }

    let currentPosition = 0
    let orderCounter = 0
    const newPoints = [...localServicePoints]

    newList.forEach(item => {
      if (item.type === 'mission') {
        currentPosition = item.idx + 1
        orderCounter = 0
      } else {
        const sp = newPoints[item.spIndex]
        newPoints[item.spIndex] = { ...sp, position: currentPosition, order: orderCounter++ }
      }
    })

    newPoints.sort((a, b) => {
      if (a.position !== b.position) return a.position - b.position
      return a.order - b.order
    })

    setLocalServicePoints(newPoints)
    const success = await saveMaintenanceServicePoints(newPoints)
    if (success === false) {
      setLocalServicePoints(localServicePoints)
    }
  }

  const missionIdsKey = scenario.missions_ids.join(',')

  const individualMissionDetails = useMemo(() => {
    const selectedIds = Array.from(selectedMissionIndices).map(idx => scenario.missions_ids[idx])
    return selectedIds.map(id => missionCacheRef.current[id]).filter(Boolean) as IMission[]
  }, [selectedMissionIndices, missionIdsKey, cacheVersion])

  const onMapInited = () => {
    const m = mapRef.current?.getMap()
    if (m) setMap(m)
  }

  // Fetch mission info for each mission_id in the scenario
  const fetchMissions = useCallback(
    async (background: boolean = false) => {
      if (!scenario.missions_ids || scenario.missions_ids.length === 0) {
        setMissions([])
        setMissionsProgress(PROGRESS_TYPES.SUCCESS)
        return
      }
      try {
        if (!background) {
          setMissionsProgress(PROGRESS_TYPES.WORK)
        }

        // Fetch only the missions that belong to the scenario.
        const promises = scenario.missions_ids.map(id => {
          if (!missionPromises[id]) {
            missionPromises[id] = MainApi.fetchMission(id).finally(() => {
              // Delay deletion slightly to catch any very close overlapping calls
              setTimeout(() => {
                delete missionPromises[id]
              }, 100)
            })
          }
          return missionPromises[id]
        })
        const results = await Promise.all(promises)

        setMissions(results.map(r => r.mission))

        // Populate cache so useMemo-based values update
        results.forEach(m => {
          missionCacheRef.current[m.mission.id] = m
        })
        setCacheVersion(v => v + 1)

        setMissionsProgress(PROGRESS_TYPES.SUCCESS)
      } catch (error) {
        setMissionsProgress(PROGRESS_TYPES.ERROR)
        console.warn('[ScenarioView] fetchMissions error:', error)
      }
    },
    [missionIdsKey]
  )

  useEffect(() => {
    fetchMissions(missions.length > 0)
  }, [fetchMissions])

  const prevMissionsLength = useRef(scenario.missions_ids.length)
  // Initialize and sync selected mission indices
  useEffect(() => {
    if (scenario.missions_ids.length > 0) {
      if (!hasInitializedSelection.current || prevMissionsLength.current === 0) {
        setSelectedMissionIndices(new Set(scenario.missions_ids.map((_, i) => i)))
        hasInitializedSelection.current = true
      } else if (scenario.missions_ids.length > prevMissionsLength.current) {
        // When missions are added, we want them to be checked by default
        setSelectedMissionIndices(prev => {
          const next = new Set(prev)
          for (let i = prevMissionsLength.current; i < scenario.missions_ids.length; i++) {
            next.add(i)
          }
          return next
        })
      }
    } else {
      setSelectedMissionIndices(new Set())
      hasInitializedSelection.current = false
    }
    prevMissionsLength.current = scenario.missions_ids.length
  }, [missionIdsKey])

  // Fetch individual mission details for checkboxes
  useEffect(() => {
    const fetchIndividual = async () => {
      if (selectedMissionIndices.size > 0) {
        try {
          const selectedIds = Array.from(selectedMissionIndices).map(idx => scenario.missions_ids[idx])
          const uniqueIds = Array.from(new Set(selectedIds))

          // Use the common promise cache
          const promises = uniqueIds.map(id => {
            if (!missionPromises[id]) {
              missionPromises[id] = MainApi.fetchMission(id).finally(() => {
                // Delay deletion slightly to catch any very close overlapping calls
                setTimeout(() => {
                  delete missionPromises[id]
                }, 100)
              })
            }
            return missionPromises[id]
          })

          const results = await Promise.all(promises)

          let updated = false
          results.forEach(m => {
            if (!missionCacheRef.current[m.mission.id]) {
              missionCacheRef.current[m.mission.id] = m
              updated = true
            }
          })
          if (updated) {
            setCacheVersion(v => v + 1)
          }
        } catch (error) {
          console.error('Failed to fetch individual mission details', error)
        }
      }
    }
    fetchIndividual()
  }, [selectedMissionIndices, missionIdsKey])

  // Fetch full mission details when a mission is selected
  useEffect(() => {
    const fetchMissionDetails = async () => {
      if (highlightedMissionId) {
        const cached = missionCacheRef.current[highlightedMissionId]
        if (cached) {
          setSelectedMissionDetails(cached)
          return
        }

        try {
          const missionData = await MainApi.fetchMission(highlightedMissionId)
          missionCacheRef.current[missionData.mission.id] = missionData
          setCacheVersion(v => v + 1)
          setSelectedMissionDetails(missionData)
        } catch (error) {
          console.error('Failed to fetch selected mission', error)
        }
      } else {
        setSelectedMissionDetails(undefined)
      }
    }
    fetchMissionDetails()
  }, [highlightedMissionId])

  // Zoom map to selected mission
  useEffect(() => {
    if (map && selectedMissionDetails) {
      const features: Feature[] = [
        ...selectedMissionDetails.cleaningZones,
        ...selectedMissionDetails.restrictZones,
        ...selectedMissionDetails.cleaningRoutes,
        ...selectedMissionDetails.transferRoutes,
        ...(selectedMissionDetails.pointRoutes || []),
      ]
      if (features.length > 0) {
        zoomMapTo(map, features, 580)
      }
    }
  }, [map, selectedMissionDetails])

  // Zoom map to all missions when details are first loaded
  const hasZoomedAll = useRef(false)
  useEffect(() => {
    if (map && individualMissionDetails.length > 0 && !hasZoomedAll.current) {
      const features: Feature[] = individualMissionDetails.flatMap(m => [
        ...m.cleaningZones,
        ...m.restrictZones,
        ...m.cleaningRoutes,
        ...m.transferRoutes,
        ...(m.pointRoutes || []),
      ])
      if (features.length > 0) {
        zoomMapTo(map, features, 580)
        hasZoomedAll.current = true
      }
    }
  }, [map, individualMissionDetails])

  const toggleShowAll = async () => {
    if (selectedMissionIndices.size === missions.length) {
      setSelectedMissionIndices(new Set())
    } else {
      setSelectedMissionIndices(new Set(missions.map((_, i) => i)))
    }
  }

  const areAllLightsOn = useMemo(() => {
    if (missions.length === 0) return false
    return missions.every((_, index) => scenario.mission_equipment_setups?.[index]?.front_lights === true)
  }, [missions, scenario.mission_equipment_setups])

  const toggleAllEquipmentSetup = async (on: boolean) => {
    try {
      const updatedSetups: Record<string, { front_lights?: boolean }> = { ...(scenario.mission_equipment_setups || {}) }
      missions.forEach((_, index) => {
        updatedSetups[index] = { ...updatedSetups[index], front_lights: on }
      })

      const patchData: any = { mission_equipment_setups: updatedSetups }
      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error toggling all equipment setups:', error)
    }
  }

  const addMission = async (mission: IMissionInfo | undefined) => {
    if (!mission) return
    const previousSPs = localServicePoints
    const previousSelected = selectedMissionIndices
    try {
      const isFirst = scenario.missions_ids.length === 0
      const updatedIds = [...scenario.missions_ids, mission.id]
      const newIndex = scenario.missions_ids.length
      const oldCount = scenario.missions_ids.length

      const updatedSetups = { ...(scenario.mission_equipment_setups || {}) }
      // Inherit the current 'All Lights' state if there are existing missions, otherwise default to false
      updatedSetups[newIndex] = { front_lights: areAllLightsOn }

      const updatedServicePoints = shiftEndBlockServicePoints(localServicePoints, oldCount)
      const chargingErr = chargingPlacementError(updatedServicePoints, updatedIds.length)
      if (chargingErr) {
        setWarningMsg(chargingErr)
        return
      }

      const maintenance = buildScenarioMaintenance(
        scenario.maintenance && typeof scenario.maintenance === 'object' ? (scenario.maintenance as any) : {},
        updatedServicePoints,
        updatedIds.length
      )

      const patchData: any = {
        missions_ids: updatedIds,
        mission_equipment_setups: updatedSetups,
        maintenance,
      }
      if (isFirst && mission.robot_type) {
        patchData.robot_type = mission.robot_type
      }
      setLocalServicePoints(updatedServicePoints)
      setSelectedMissionIndices(prev => {
        const next = new Set(prev)
        next.add(newIndex)
        return next
      })
      await MainApi.patchScenarios(scenario.id, patchData)
      setAddMissionDialog(false)
      onUpdated(true)
    } catch (error) {
      console.error('Error adding mission:', error)
      setLocalServicePoints(previousSPs)
      setSelectedMissionIndices(previousSelected)
      setWarningMsg(formatApiErrorDetails(error))
    }
  }

  const excludeMission = async (index: number) => {
    const newList = combinedList.filter(item => !(item.type === 'mission' && item.idx === index))
    if (scenario.missions_ids.length > 1 && newList.length > 0 && newList[0].type !== 'mission') {
      setWarningMsg('A scenario must begin with a mission')
      return
    }
    if (hasChargingViolation(newList)) {
      setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
      return
    }

    const { updatedIds, newSetups, newServicePoints } = deriveStateFromCombinedList(newList)
    const chargingErr = chargingPlacementError(newServicePoints, updatedIds.length)
    if (chargingErr) {
      setWarningMsg(chargingErr)
      return
    }

    const previousMissions = missions
    const previousSPs = localServicePoints
    const previousSelected = selectedMissionIndices
    const maintenance = buildScenarioMaintenance(
      scenario.maintenance && typeof scenario.maintenance === 'object' ? (scenario.maintenance as any) : {},
      newServicePoints,
      updatedIds.length
    )

    const patchData: any = {
      missions_ids: updatedIds,
      mission_equipment_setups: newSetups,
      maintenance,
    }

    setMissions(prev => prev.filter((_, i) => i !== index))
    setSelectedMissionIndices(prev => {
      const next = new Set<number>()
      prev.forEach(i => {
        if (i < index) next.add(i)
        else if (i > index) next.add(i - 1)
      })
      return next
    })
    setLocalServicePoints(newServicePoints)

    try {
      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error excluding mission:', error)
      setMissions(previousMissions)
      setLocalServicePoints(previousSPs)
      setSelectedMissionIndices(previousSelected)
      setWarningMsg(formatApiErrorDetails(error))
      fetchMissions(true)
    }
  }

  const moveMission = async (index: number, direction: 'up' | 'down') => {
    // Find this mission in the combined list
    const idxInList = combinedList.findIndex(item => item.type === 'mission' && item.idx === index)
    if (idxInList === -1) return

    const targetIdx = direction === 'up' ? idxInList - 1 : idxInList + 1
    if (targetIdx < 0 || targetIdx >= combinedList.length) return

    // Swap in combined list
    const newList = [...combinedList]
    ;[newList[idxInList], newList[targetIdx]] = [newList[targetIdx], newList[idxInList]]

    // Constraint: first item must be a mission
    if (newList[0].type !== 'mission') {
      setWarningMsg('A scenario must begin with a mission.')
      return
    }

    // Constraint: charging SP must remain at the end
    if (hasChargingViolation(newList)) {
      setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
      return
    }

    // Derive new state
    const { updatedIds, newSetups, newServicePoints, missionIndexMap } = deriveStateFromCombinedList(newList)
    const chargingErr = chargingPlacementError(newServicePoints, updatedIds.length)
    if (chargingErr) {
      setWarningMsg(chargingErr)
      return
    }

    // Optimistic UI updates
    const updatedMissions = updatedIds.map(id => missions.find(m => m.id === id)!)
    setMissions(updatedMissions)

    // Remap checkboxes
    setSelectedMissionIndices(prev => {
      const next = new Set<number>()
      prev.forEach(i => {
        const newIdx = missionIndexMap.get(i)
        if (newIdx != null) next.add(newIdx)
      })
      return next
    })

    setLocalServicePoints(newServicePoints)

    const previousMissions = missions
    const previousSPs = localServicePoints
    const previousSelected = selectedMissionIndices
    const maintenance = buildScenarioMaintenance(
      scenario.maintenance && typeof scenario.maintenance === 'object' ? (scenario.maintenance as any) : {},
      newServicePoints,
      updatedIds.length
    )

    try {
      await MainApi.patchScenarios(scenario.id, {
        missions_ids: updatedIds,
        mission_equipment_setups: newSetups,
        maintenance,
      } as any)
      onUpdated(true)
    } catch (error) {
      console.error('Error moving mission:', error)
      setMissions(previousMissions)
      setLocalServicePoints(previousSPs)
      setSelectedMissionIndices(previousSelected)
      setWarningMsg(formatApiErrorDetails(error))
      fetchMissions(true)
    }
  }

  const toggleEquipmentSetup = async (index: number) => {
    try {
      const isCurrentlyOn = scenario.mission_equipment_setups?.[index]?.front_lights ?? false
      const updatedSetups = { ...(scenario.mission_equipment_setups || {}) }

      updatedSetups[index] = { front_lights: !isCurrentlyOn }

      const patchData: any = { mission_equipment_setups: updatedSetups }
      await MainApi.patchScenarios(scenario.id, patchData)
      onUpdated(true)
    } catch (error) {
      console.error('Error toggling equipment setup:', error)
    }
  }

  const [draggedIndex, setDraggedIndex] = useState<number | null>(null)
  const [draggedSPIndex, setDraggedSPIndex] = useState<number | null>(null)
  const [dragOverIndex, setDragOverIndex] = useState<number | null>(null)
  const [dragOverSPIndex, setDragOverSPIndex] = useState<number | null>(null)
  const [dragOverSPPosition, setDragOverSPPosition] = useState<'top' | 'bottom' | null>(null)
  const [dragOverMissionIndex, setDragOverMissionIndex] = useState<number | null>(null)
  const [dragOverMissionPosition, setDragOverMissionPosition] = useState<'top' | 'bottom' | null>(null)

  const handleDragStart = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.dataTransfer.setData('text/plain', index.toString())
    e.dataTransfer.effectAllowed = 'move'
    setDraggedIndex(index)
  }

  const handleSPDragStart = (e: React.DragEvent<HTMLDivElement>, spIndex: number) => {
    e.dataTransfer.setData('application/sp-index', spIndex.toString())
    e.dataTransfer.effectAllowed = 'move'
    setDraggedSPIndex(spIndex)
  }

  const handleDragOver = (e: React.DragEvent<HTMLDivElement>, index: number) => {
    e.preventDefault()
    e.dataTransfer.dropEffect = 'move'
    setDragOverSPIndex(null)
    setDragOverSPPosition(null)

    const rect = e.currentTarget.getBoundingClientRect()
    const midPoint = rect.top + rect.height / 2
    let pos: 'top' | 'bottom' = e.clientY < midPoint ? 'top' : 'bottom'

    // Top half → insert before this row (index), bottom half → insert after this row (index + 1)
    let dropIndex = e.clientY < midPoint ? index : index + 1
    if (draggedSPIndex !== null && index === 0 && pos === 'top') {
      pos = 'bottom'
      dropIndex = 1
    }

    setDragOverIndex(dropIndex)
    setDragOverMissionIndex(index)
    setDragOverMissionPosition(pos)
  }

  const handleSPDragOver = (e: React.DragEvent<HTMLDivElement>, spIndex: number, position: number) => {
    e.preventDefault()
    e.stopPropagation()
    e.dataTransfer.dropEffect = 'move'

    const rect = e.currentTarget.getBoundingClientRect()
    const midPoint = rect.top + rect.height / 2
    const pos: 'top' | 'bottom' = e.clientY < midPoint ? 'top' : 'bottom'

    setDragOverSPIndex(spIndex)
    setDragOverSPPosition(pos)
    setDragOverIndex(position)
    setDragOverMissionIndex(null)
    setDragOverMissionPosition(null)
  }

  const handleDragEnd = (e: React.DragEvent<HTMLDivElement>) => {
    setDraggedIndex(null)
    setDraggedSPIndex(null)
    setDragOverIndex(null)
    setDragOverSPIndex(null)
    setDragOverSPPosition(null)
    setDragOverMissionIndex(null)
    setDragOverMissionPosition(null)
  }

  const handleDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault()
    e.stopPropagation()
    if (dragOverIndex == null) return
    const dropIndex = dragOverIndex

    const spIndexStr = e.dataTransfer.getData('application/sp-index')
    if (spIndexStr) {
      const spIndex = parseInt(spIndexStr, 10)
      const sp = localServicePoints[spIndex]
      if (sp) {
        const newPoints = [...localServicePoints]

        if (scenario.missions_ids.length > 0 && dragOverSPIndex == null && dropIndex === 0) {
          setWarningMsg('A scenario must begin with a mission')
          setDraggedSPIndex(null)
          setDragOverIndex(null)
          setDragOverSPIndex(null)
          setDragOverSPPosition(null)
          setDragOverMissionIndex(null)
          setDragOverMissionPosition(null)
          return
        }

        if (dragOverSPIndex != null) {
          const targetSP = localServicePoints[dragOverSPIndex]
          let newOrder = targetSP.order - 0.5
          if (dragOverSPPosition === 'bottom') {
            newOrder = targetSP.order + 0.5
          }
          newPoints[spIndex] = { ...sp, position: Math.max(1, targetSP.position), order: newOrder }
        } else {
          const targetPosition = Math.max(1, dropIndex)
          const pointsAtPos = localServicePoints.filter((p, idx) => p.position === targetPosition && idx !== spIndex)
          let newOrder = 0
          if (pointsAtPos.length > 0) {
            if (dragOverMissionPosition === 'bottom') {
              newOrder = Math.min(...pointsAtPos.map(p => p.order)) - 0.5
            } else {
              const chargingSP = pointsAtPos.find(p => isChargingAnchor(p, lotModels))
              if (chargingSP && !isChargingAnchor(sp, lotModels)) {
                newOrder = chargingSP.order - 0.5
              } else {
                newOrder = Math.max(...pointsAtPos.map(p => p.order)) + 0.5
              }
            }
          }
          newPoints[spIndex] = { ...sp, position: targetPosition, order: newOrder }
        }

        // Reorder
        let currentPosition = -1
        let orderCounter = 0
        newPoints.sort((a, b) => {
          if (a.position !== b.position) return a.position - b.position
          return a.order - b.order
        })

        const fixedPoints = newPoints.map(p => {
          if (p.position !== currentPosition) {
            currentPosition = p.position
            orderCounter = 0
          }
          return { ...p, order: orderCounter++ }
        })

        const spDropOrder: Array<{ type: 'mission' } | { type: 'sp'; sp: IServicePoint }> = []
        for (let i = 0; i < missions.length; i++) {
          fixedPoints
            .filter(p => p.position === i)
            .sort((a, b) => a.order - b.order)
            .forEach(p => spDropOrder.push({ type: 'sp', sp: p }))
          spDropOrder.push({ type: 'mission' })
        }
        fixedPoints
          .filter(p => p.position >= missions.length)
          .sort((a, b) => a.order - b.order)
          .forEach(p => spDropOrder.push({ type: 'sp', sp: p }))
        if (hasChargingNotAtEnd(spDropOrder, lotModels)) {
          setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
          setDraggedSPIndex(null)
          setDragOverIndex(null)
          setDragOverSPIndex(null)
          setDragOverSPPosition(null)
          setDragOverMissionIndex(null)
          setDragOverMissionPosition(null)
          return
        }

        setLocalServicePoints(fixedPoints)
        const success = await saveMaintenanceServicePoints(fixedPoints)
        if (success === false) {
          setLocalServicePoints(localServicePoints)
        }
      }
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    const startIndexStr = e.dataTransfer.getData('text/plain')
    if (!startIndexStr) {
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }
    const startIndex = parseInt(startIndexStr, 10)

    // Build a new combined list with the mission at its new position
    const idxInList = combinedList.findIndex(item => item.type === 'mission' && item.idx === startIndex)
    if (idxInList === -1) {
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    // Determine target position in the combined list
    let targetCombinedIdx: number
    if (dragOverSPIndex != null) {
      // Dropped onto an SP row
      const spCombinedIdx = combinedList.findIndex(item => item.type === 'sp' && item.spIndex === dragOverSPIndex)
      targetCombinedIdx = dragOverSPPosition === 'bottom' ? spCombinedIdx + 1 : spCombinedIdx
    } else if (dragOverMissionIndex != null) {
      // Dropped onto a mission row
      const mCombinedIdx = combinedList.findIndex(item => item.type === 'mission' && item.idx === dragOverMissionIndex)
      targetCombinedIdx = dragOverMissionPosition === 'bottom' ? mCombinedIdx + 1 : mCombinedIdx
    } else {
      // Fallback: use dropIndex to find position (end of list)
      targetCombinedIdx = combinedList.length
    }

    // Remove mission from old position and insert at new position
    const newList = [...combinedList]
    const [movedItem] = newList.splice(idxInList, 1)
    // Adjust target if removed item was before target
    const adjustedTarget = idxInList < targetCombinedIdx ? targetCombinedIdx - 1 : targetCombinedIdx
    newList.splice(adjustedTarget, 0, movedItem)

    // Check if no actual change
    if (idxInList === adjustedTarget) {
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    // Constraint: first item must be a mission
    if (newList[0].type !== 'mission') {
      setWarningMsg('A scenario must begin with a mission')
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    // Constraint: charging SP must remain at the end
    if (hasChargingViolation(newList)) {
      setWarningMsg(CHARGING_MUST_BE_LAST_MSG)
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    // Derive new state from the reordered combined list
    const { updatedIds, newSetups, newServicePoints, missionIndexMap } = deriveStateFromCombinedList(newList)
    const chargingErr = chargingPlacementError(newServicePoints, updatedIds.length)
    if (chargingErr) {
      setWarningMsg(chargingErr)
      setDraggedIndex(null)
      setDraggedSPIndex(null)
      setDragOverIndex(null)
      setDragOverSPIndex(null)
      setDragOverSPPosition(null)
      setDragOverMissionIndex(null)
      setDragOverMissionPosition(null)
      return
    }

    // Optimistic UI updates
    const updatedMissions = updatedIds.map(id => missions.find(m => m.id === id)!)
    setMissions(updatedMissions)

    // Remap checkboxes
    const nextSelected = new Set<number>()
    selectedMissionIndices.forEach(i => {
      const newIdx = missionIndexMap.get(i)
      if (newIdx != null) nextSelected.add(newIdx)
    })
    setSelectedMissionIndices(nextSelected)

    setLocalServicePoints(newServicePoints)

    setDraggedIndex(null)
    setDraggedSPIndex(null)
    setDragOverIndex(null)
    setDragOverSPIndex(null)
    setDragOverSPPosition(null)
    setDragOverMissionIndex(null)
    setDragOverMissionPosition(null)

    const previousMissions = missions
    const previousSPs = localServicePoints
    const previousSelected = selectedMissionIndices
    const maintenance = buildScenarioMaintenance(
      scenario.maintenance && typeof scenario.maintenance === 'object' ? (scenario.maintenance as any) : {},
      newServicePoints,
      updatedIds.length
    )

    try {
      await MainApi.patchScenarios(scenario.id, {
        missions_ids: updatedIds,
        mission_equipment_setups: newSetups,
        maintenance,
      } as any)
      onUpdated(true)
    } catch (error) {
      console.error('Error dragging mission:', error)
      setMissions(previousMissions)
      setLocalServicePoints(previousSPs)
      setSelectedMissionIndices(previousSelected)
      setWarningMsg(formatApiErrorDetails(error))
      fetchMissions(true)
    }
  }

  const isScenarioEndServicePoint = (
    sp: IServicePoint,
    spIndex: number,
    points: IServicePoint[],
    missionCount: number
  ) => {
    if (sp.position < missionCount) {
      return false
    }
    const endPoints = points
      .map((point, index) => ({ point, index }))
      .filter(({ point }) => point.position >= missionCount)
    const lastEnd = endPoints[endPoints.length - 1]
    return lastEnd?.index === spIndex
  }

  const getSPDisplayProps = (sp: IServicePoint, isLast: boolean) => {
    let typeLabel = 'Service Point'
    let icon: any = SandClockSvg
    let desc = ''
    let allowedActions: ActionType[] = []
    let allowSingleActionChips = false

    if (sp.kind === SERVICE_POINT_KINDS.WAIT) {
      typeLabel = 'Waiting Point'
      icon = SandClockSvg
    } else if (sp.kind === SERVICE_POINT_KINDS.STATION_LOT) {
      const lot = facilityLots.find(l =>
        sp.lot_pk ? l.id === sp.lot_pk : l.lot_model === sp.lot_model && (!sp.lot_id || l.lot_id === sp.lot_id)
      )
      typeLabel = lot ? lot.name || lot.robot_station_name || lot.lot_model : 'Station Lot'

      const modelConfig = findLotModelByNumber(lotModels, sp.lot_model)
      if (modelConfig) {
        const hasChargingLot = localServicePoints.some(p => {
          if (p.kind !== SERVICE_POINT_KINDS.STATION_LOT) return false
          const cfg = findLotModelByNumber(lotModels, p.lot_model)
          return cfg?.type === LotType.CHARGING
        })
        allowedActions = resolveScenarioStationLotAllowedActions(modelConfig, {
          isLast,
          hasChargingLotInScenario: hasChargingLot,
          robotType: scenario.robot_type,
        })
        allowSingleActionChips = modelConfig.type === LotType.REFILLING && !isLast && allowedActions.length === 1
        if (modelConfig.type === LotType.CHARGING) icon = ChargingSvg
        else if (modelConfig.type === LotType.WASHING) icon = WashingSvg
        else if (modelConfig.type === LotType.PARKING) icon = ParkingSvg
        else if (modelConfig.type === LotType.REFILLING) icon = RefillingSvg
        else if (modelConfig.type === LotType.DEBRIS_DUMP) icon = DebrisDumpSvg
      } else {
        icon = LotNotSetSvg
      }
    } else if (sp.kind === SERVICE_POINT_KINDS.MAINTENANCE_POINT) {
      const mp = maintenancePoints.find(p => p.facility_index === sp.maintenance_point_id)
      if (mp) {
        typeLabel =
          mp.name ||
          mp.type
            .split('_')
            .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(' ')

        if (mp.type === 'waste_water_drain') {
          icon = WasteWaterDrainSvg
          allowedActions = [ActionType.WASTE_WATER_DRAIN]
        } else if (mp.type === 'debris_dump') {
          icon = DebrisDumpSvg
          allowedActions = [ActionType.DEBRIS_DUMP]
        } else if (mp.type === 'universal') {
          icon = MaintenanceUniversalSvg
          allowedActions = [ActionType.WASTE_WATER_DRAIN, ActionType.DEBRIS_DUMP]
        } else {
          icon = ListAltIcon as any
          allowedActions = [ActionType.WASTE_WATER_DRAIN, ActionType.DEBRIS_DUMP]
        }
      } else {
        typeLabel = 'Maintenance Point'
        icon = ListAltIcon as any
        allowedActions = [ActionType.WASTE_WATER_DRAIN, ActionType.DEBRIS_DUMP]
      }
    }

    return {
      typeLabel,
      icon,
      desc,
      allowedActions: filterCatalogActionsForRobotType(allowedActions, scenario.robot_type),
      allowSingleActionChips,
    }
  }

  return (
    <div className={style.container}>
      <div className={style.main}>
        <div className={style.headerArea}>
          <div className={clsx(style.titleBlock)}>
            <div className={style.nameRow}>
              <Title text={scenario.name} />
              <div className={style.titleId}>
                {t('missions.id')}: {scenario.id}
              </div>
              {!isMobile && (
                <RobotTypeLabel
                  vendor={VENDOR.AUTONOMICS}
                  robotType={scenario.robot_type}
                  inconsistent={scenario.missions_type_inconsistent}
                />
              )}
            </div>
            <div className={style.maintenanceButtonsGroup}>
              <Tooltip
                title={missions.length === 0 ? 'Add a mission to the scenario first' : ''}
                placement="bottom"
                enterDelay={300}
              >
                <span style={{ display: 'inline-flex' }}>
                  <Button.Outlined
                    className={style.button}
                    style={{
                      width: isMobile ? 'auto' : '180px',
                      minWidth: isMobile ? 'auto' : '180px',
                      padding: '0 8px',
                    }}
                    onClick={handleOpenAddSPMenu}
                    disabled={missions.length === 0}
                  >
                    Add a Service Point
                  </Button.Outlined>
                </span>
              </Tooltip>
              <StyledDropdown anchorEl={addSPAnchorEl} open={Boolean(addSPAnchorEl)} onClose={handleCloseAddSPMenu}>
                <MenuItem
                  onClick={() =>
                    handleAddSP({
                      kind: SERVICE_POINT_KINDS.WAIT,
                      position: missions.length,
                      order: 999,
                      action_type: [ActionType.WAITING],
                      time_sec: 60,
                    })
                  }
                >
                  <ListItemIcon>
                    <SandClockSvg width={20} height={20} />
                  </ListItemIcon>
                  <ListItemText>Waiting Point</ListItemText>
                </MenuItem>
                {facilityLots.map(lot => {
                  if (!washingLotVisibleForScenarioRobot(lot.lot_model, scenario.robot_type)) {
                    return null
                  }
                  const modelConfig = findLotModelByNumber(lotModels, lot.lot_model)
                  const allowedActions = filterCatalogActionsForRobotType(
                    modelConfig ? modelConfig.allowed_actions : [],
                    scenario.robot_type
                  )
                  if (
                    modelConfig &&
                    allowedActions.length === 0 &&
                    modelConfig.type !== LotType.REFILLING
                  ) {
                    return null
                  }

                  let Icon = LotNotSetSvg
                  if (modelConfig) {
                    if (modelConfig.type === LotType.CHARGING) Icon = ChargingSvg
                    else if (modelConfig.type === LotType.WASHING) Icon = WashingSvg
                    else if (modelConfig.type === LotType.PARKING) Icon = ParkingSvg
                    else if (modelConfig.type === LotType.REFILLING) Icon = RefillingSvg
                    else if (modelConfig.type === LotType.DEBRIS_DUMP) Icon = DebrisDumpSvg
                  }

                  return (
                    <MenuItem
                      key={`lot-${lot.id}`}
                      onClick={() =>
                        handleAddSP({
                          kind: SERVICE_POINT_KINDS.STATION_LOT,
                          position: missions.length,
                          order: 999,
                          action_type:
                            modelConfig?.type === LotType.REFILLING
                              ? []
                              : allowedActions.length === 1
                              ? [allowedActions[0]]
                              : [],
                          lot_model: lot.lot_model,
                          lot_id: lot.lot_id,
                          lot_pk: lot.id,
                          station_id: lot.robot_station_hardware_id || '',
                        })
                      }
                    >
                      <ListItemIcon>
                        <Icon width={20} height={20} />
                      </ListItemIcon>
                      <ListItemText>{lot.name || lot.robot_station_name || lot.lot_model}</ListItemText>
                    </MenuItem>
                  )
                })}
                {maintenancePoints
                  .filter(
                    mp =>
                      !isSweeperRobotType(scenario.robot_type) || !isDrainOnlyMaintenancePointType(mp.type)
                  )
                  .map(mp => {
                  let MPIcon: any = ListAltIcon
                  if (mp.type === 'waste_water_drain') MPIcon = WasteWaterDrainSvg
                  else if (mp.type === 'debris_dump') MPIcon = DebrisDumpSvg
                  else if (mp.type === 'universal') MPIcon = MaintenanceUniversalSvg

                  const mpTypeLabel = mp.type
                    .split('_')
                    .map((word: string) => word.charAt(0).toUpperCase() + word.slice(1))
                    .join(' ')

                  return (
                    <MenuItem
                      key={`mp-${mp.id}`}
                      onClick={() =>
                        handleAddSP({
                          kind: SERVICE_POINT_KINDS.MAINTENANCE_POINT,
                          position: missions.length,
                          order: 999,
                          action_type:
                            mp.type === 'waste_water_drain'
                              ? [ActionType.WASTE_WATER_DRAIN]
                              : mp.type === 'debris_dump'
                              ? [ActionType.DEBRIS_DUMP]
                              : mp.type === 'universal'
                              ? isSweeperRobotType(scenario.robot_type)
                                ? [ActionType.DEBRIS_DUMP]
                                : []
                              : [],
                          maintenance_point_id: mp.facility_index ?? undefined,
                        })
                      }
                    >
                      <ListItemIcon>
                        <MPIcon width={20} height={20} />
                      </ListItemIcon>
                      <ListItemText>{mp.name?.trim() || mpTypeLabel}</ListItemText>
                    </MenuItem>
                  )
                })}
              </StyledDropdown>
              <Button.Standart
                className={style.button}
                style={{ width: isMobile ? 'auto' : '250px' }}
                onClick={() => setAddMissionDialog(true)}
              >
                Add a Mission
              </Button.Standart>
              {FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS && (
                <Button.Outlined
                  className={clsx(style.button, style.prerequisitesGearBtn)}
                  onClick={() => setPrerequisitesModalOpen(true)}
                  title={t('prerequisites.title')}
                >
                  <GearSvg />
                </Button.Outlined>
              )}
            </div>
          </div>
        </div>
        <Box className={clsx(style.box, { [style.boxHidden]: isMobile && !showTable })}>
          <MissionStats
            coverage={scenario.overall_cleaned_area || 0}
            length={scenario.overall_length || 0}
            time={scenario.overall_execution_time || 0}
          />
          {FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS && (
            <ResourceProfileSummary
              profile={scenario.resource_profile}
              hideDirtyWater={isSweeperRobotType(scenario.robot_type)}
            />
          )}

          {missionsProgress === PROGRESS_TYPES.WORK && <Loader />}
          {missionsProgress === PROGRESS_TYPES.ERROR && (
            <div className={style.emptyWrapper}>
              <div className={style.emptyTitle}>Error loading missions</div>
            </div>
          )}
          <MissionsTable
            allChecked={missions.length > 0 && selectedMissionIndices.size === missions.length}
            onToggleAll={toggleShowAll}
            showLightsToggle={true}
            allLightsOn={areAllLightsOn}
            onToggleAllLights={toggleAllEquipmentSetup}
            hideDragging={isMobile}
            onTableDragOver={e => {
              e.preventDefault()
              e.dataTransfer.dropEffect = 'move'
            }}
            onTableDrop={handleDrop}
          >
            {missions.map((mission, index) => (
              <React.Fragment key={`${mission.id}-${index}`}>
                {localServicePoints
                  ?.map((sp, spIndex) => ({ sp, spIndex }))
                  .filter(({ sp }) => sp.position === index)
                  .sort((a, b) => a.sp.order - b.sp.order)
                  .map(({ sp, spIndex }) => {
                    const isLast = isScenarioEndServicePoint(sp, spIndex, localServicePoints ?? [], missions.length)
                    const { typeLabel, icon, desc, allowedActions, allowSingleActionChips } = getSPDisplayProps(
                      sp,
                      isLast
                    )

                    return (
                      <MaintenanceItem
                        key={`sp-${spIndex}`}
                        type={typeLabel}
                        description={desc}
                        icon={icon}
                        hideDragging={isMobile}
                        dragIndicator={dragOverSPIndex === spIndex ? dragOverSPPosition || 'none' : 'none'}
                        onDragStart={e => handleSPDragStart(e, spIndex)}
                        onDragOver={e => handleSPDragOver(e, spIndex, index)}
                        onDrop={handleDrop}
                        onDragEnd={handleDragEnd}
                        isDragging={draggedSPIndex === spIndex}
                        onDelete={() => deleteServicePoint(spIndex)}
                        time={sp.kind === SERVICE_POINT_KINDS.WAIT ? sp.time_sec : undefined}
                        onTimeChange={newTime => updateWaitingTime(spIndex, newTime)}
                        allowedActions={allowedActions}
                        allowSingleActionChips={allowSingleActionChips}
                        selectedActions={filterCatalogActionsForRobotType(sp.action_type, scenario.robot_type)}
                        onActionToggle={action => handleActionToggle(spIndex, action)}
                        onMoveUp={() => moveServicePoint(spIndex, 'up')}
                        onMoveDown={() => moveServicePoint(spIndex, 'down')}
                        isFirst={getSPIsFirst(spIndex)}
                        isLast={getSPIsLast(spIndex)}
                        onClick={() => setHighlightedSpIndex(spIndex)}
                        highlighted={highlightedSpIndex === spIndex}
                      />
                    )
                  })}
                <MissionTableRow
                  mission={mission}
                  onClick={() => setHighlightedMissionId(mission.id)}
                  highlighted={highlightedMissionId === mission.id}
                  onExclude={() => excludeMission(index)}
                  onMoveUp={() => moveMission(index, 'up')}
                  onMoveDown={() => moveMission(index, 'down')}
                  isFirst={getMissionIsFirst(index)}
                  isLast={getMissionIsLast(index)}
                  location={scenario.missions_locations?.[index]}
                  checked={selectedMissionIndices.has(index)}
                  onCheckboxChange={isChecked => {
                    const newSet = new Set(selectedMissionIndices)
                    if (isChecked) {
                      newSet.add(index)
                    } else {
                      newSet.delete(index)
                    }
                    setSelectedMissionIndices(newSet)
                  }}
                  typeMismatch={Boolean(
                    scenario.missions_type_inconsistent &&
                      scenario.robot_type != null &&
                      mission.robot_type !== scenario.robot_type
                  )}
                  equipmentSetup={scenario.mission_equipment_setups?.[index]}
                  onEquipmentSetupChange={() => toggleEquipmentSetup(index)}
                  index={index}
                  onDragStart={handleDragStart}
                  onDragOver={handleDragOver}
                  onDrop={handleDrop}
                  onDragEnd={handleDragEnd}
                  isDragging={draggedIndex === index}
                  hideDragging={isMobile}
                  dragIndicator={
                    dragOverSPIndex != null
                      ? 'none'
                      : draggedSPIndex !== null
                      ? dragOverMissionIndex === index
                        ? dragOverMissionPosition || 'none'
                        : 'none'
                      : draggedIndex != null && (dragOverIndex === draggedIndex || dragOverIndex === draggedIndex + 1)
                      ? 'none'
                      : dragOverIndex === index
                      ? 'top'
                      : dragOverIndex === index + 1
                      ? 'bottom'
                      : 'none'
                  }
                />
              </React.Fragment>
            ))}
            {localServicePoints
              ?.map((sp, spIndex) => ({ sp, spIndex }))
              .filter(({ sp }) => sp.position >= missions.length)
              .sort((a, b) => a.sp.order - b.sp.order)
              .map(({ sp, spIndex }) => {
                const isLast = isScenarioEndServicePoint(sp, spIndex, localServicePoints ?? [], missions.length)
                const { typeLabel, icon, desc, allowedActions, allowSingleActionChips } = getSPDisplayProps(sp, isLast)

                return (
                  <MaintenanceItem
                    key={`sp-${spIndex}`}
                    type={typeLabel}
                    description={desc}
                    icon={icon}
                    hideDragging={isMobile}
                    isFloating={false}
                    dragIndicator={
                      dragOverSPIndex === spIndex
                        ? dragOverSPPosition || 'none'
                        : dragOverIndex === missions.length &&
                          dragOverSPIndex == null &&
                          spIndex === localServicePoints.length - 1
                        ? 'none'
                        : 'none'
                    }
                    onDragStart={e => handleSPDragStart(e, spIndex)}
                    onDragOver={e => handleSPDragOver(e, spIndex, missions.length)}
                    onDrop={handleDrop}
                    onDragEnd={handleDragEnd}
                    isDragging={draggedSPIndex === spIndex}
                    onDelete={() => deleteServicePoint(spIndex)}
                    time={sp.kind === SERVICE_POINT_KINDS.WAIT ? sp.time_sec : undefined}
                    onTimeChange={newTime => updateWaitingTime(spIndex, newTime)}
                    allowedActions={allowedActions}
                    allowSingleActionChips={allowSingleActionChips}
                    selectedActions={filterCatalogActionsForRobotType(sp.action_type, scenario.robot_type)}
                    onActionToggle={action => handleActionToggle(spIndex, action)}
                    onMoveUp={() => moveServicePoint(spIndex, 'up')}
                    onMoveDown={() => moveServicePoint(spIndex, 'down')}
                    isFirst={getSPIsFirst(spIndex)}
                    isLast={getSPIsLast(spIndex)}
                    onClick={() => setHighlightedSpIndex(spIndex)}
                    highlighted={highlightedSpIndex === spIndex}
                  />
                )
              })}
          </MissionsTable>
          <div ref={bottomRef} />
        </Box>

        {/* Map */}
        <div className={style.mapWrapper}>
          <MapboxCommon ref={mapRef} disableRuler={false} overlayItems={[]} onMapInited={onMapInited}>
            {individualMissionDetails.length > 0 && (
              <>
                <RestrictZonesOnMap restrictZones={individualMissionDetails.flatMap(m => m.restrictZones)} />
                <CleaningZonesOnMap cleaningZones={individualMissionDetails.flatMap(m => m.cleaningZones)} />
                <CleaningRoutesOnMap cleaningRoutes={individualMissionDetails.flatMap(m => m.cleaningRoutes)} />
                <TransferRoutesOnMap
                  transferRoutes={individualMissionDetails.flatMap(m => [
                    ...m.transferRoutes,
                    ...(m.pointRoutes || []),
                  ])}
                />
                <CleaningPointsOnMap
                  cleaningRoutes={individualMissionDetails.flatMap(m => m.cleaningRoutes)}
                  launchPoints={individualMissionDetails.flatMap(m => m.launchPoints)}
                />
                <CleaningZonesNamesOnMap cleaningZones={individualMissionDetails.flatMap(m => m.cleaningZones)} />
                <MissionStartFinishPoints
                  startPoints={individualMissionDetails.flatMap(m => m.startingPoint)}
                  finishPoints={individualMissionDetails.flatMap(m => m.finishingPoint)}
                />
              </>
            )}
            {scenario.facility_id && localServicePoints.length > 0 && (
              <ScenarioServicePointsOnMap
                map={map}
                servicePoints={localServicePoints}
                facilityLots={facilityLots}
                maintenancePoints={maintenancePoints}
                stations={facilityStations}
                highlightedSpIndex={highlightedSpIndex}
                onSelectSpIndex={setHighlightedSpIndex}
              />
            )}
          </MapboxCommon>
        </div>
      </div>
      <SelectMissionModal
        isOpen={addMissionDialog}
        onClose={() => setAddMissionDialog(false)}
        onConfirmClick={addMission}
        robotType={scenario.robot_type && scenario.robot_type !== 'Unknown' ? scenario.robot_type : undefined}
        only_ok_missions={false}
      />
      <ConfirmDialog
        isOpen={!!warningMsg}
        onClose={() => setWarningMsg(null)}
        onConfirmClick={() => setWarningMsg(null)}
        title="Warning"
        showCancelButton={false}
      >
        <div style={{ padding: '20px 0' }}>{warningMsg}</div>
      </ConfirmDialog>
      {FEATURE_AUTONOMICS_CONSUMABLES_AND_PROFILE_THRESHOLDS && (
        <PrerequisitesSettingsModal
          isOpen={prerequisitesModalOpen}
          value={prerequisites}
          profile={scenario.resource_profile}
          hideDirtyWater={isSweeperRobotType(scenario.robot_type)}
          onSave={savePrerequisites}
          onClose={() => setPrerequisitesModalOpen(false)}
        />
      )}
      {isMobile && (
        <IconButton
          className={MapTableToggleStyle.toggle}
          onClick={() => setShowTable(prev => !prev)}
          title={showTable ? 'Switch to Map' : 'Switch to Table'}
          style={{
            position: 'absolute',
            bottom: '24px',
            right: '24px',
            zIndex: 30,
          }}
        >
          {showTable ? (
            <MapIcon className={MapTableToggleStyle.icon} />
          ) : (
            <ListAltIcon className={MapTableToggleStyle.icon} />
          )}
        </IconButton>
      )}
    </div>
  )
}

export default ScenarioView
